import clsx from "clsx";
import {
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ErrorMessage } from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";

const variantClassMapping = {
  light: {
    label: "text-foreground-primary",
    container: "text-foreground-primary",
    // No border at rest (DS: flat filled box) - focus draws an offset ring
    // instead of swapping a border color, matching the DS's "focus-ring" box.
    input:
      "text-foreground-primary focus:ring-2 focus:ring-foreground-primary focus:ring-offset-4",
    box: "h-8 w-7.5 rounded-md bg-surface-tertiary",
  },
  dark: {
    label: "text-foreground-primary-inverse",
    container: "text-foreground-primary-inverse",
    input:
      "text-foreground-primary-inverse focus:ring-2 focus:ring-foreground-primary-inverse focus:ring-offset-4",
    box: "h-8 w-7.5 rounded-md bg-surface-tertiary-inverse",
  },
};

export type TwoFactorConfirmationCodeForm = {
  code: string;
};

export const CONFIRMATION_CODE_LENGTH = 6;

export const TwoFactorConfirmationInput = ({
  error,
  clearError,
  variant = "light",
}: {
  error?: string;
  clearError?: () => void;
  variant?: "light" | "dark";
}) => {
  const { t } = useTranslation();
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const {
    setValue,
    formState: { errors },
    clearErrors,
    control,
  } = useFormContext<TwoFactorConfirmationCodeForm>();

  const hasError = !!errors.code || !!error;
  const code = useWatch({ control, name: "code" });

  // When code is reset to "", clear DOM input values
  useEffect(() => {
    if (code === "") {
      inputRefs.current.forEach((input) => (input.value = ""));
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  }, [code]);

  // On backspace keydown, we want to delete to the previous input field
  // if the field is already empty.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && (e.target as HTMLInputElement).value === "") {
      const prevInput = inputRefs.current[Math.max(0, index - 1)];
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  // On input, we want to move to the next input field if we get a character
  // and the field is not already holding a value.
  const handleInput = useCallback(
    (e: FormEvent<HTMLInputElement>, index: number) => {
      const inputElement = e.target as HTMLInputElement;
      const [firstCharacter = "", ...remainingInput] = inputElement.value;

      inputElement.value = firstCharacter;
      // Emptied boxes must shorten the code too, otherwise a stale digit is submitted.
      setValue("code", inputRefs.current.map((input) => input.value).join(""));

      if (!firstCharacter) {
        return;
      }

      clearErrors("code");

      if (clearError) {
        clearError();
      }

      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        nextInput.focus();
        nextInput.value = remainingInput.join("");
        nextInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    },
    [setValue, clearErrors, clearError],
  );

  // On paste, we want to paste the first 6 characters into the input fields
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasteData =
      e.clipboardData?.getData("text").slice(0, CONFIRMATION_CODE_LENGTH) ?? "";
    inputRefs.current.forEach((input, i) => {
      input.value = pasteData[i] || "";
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
  };

  const variantClasses = variantClassMapping[variant];

  return (
    <fieldset>
      <label
        className={clsx(
          "flex flex-col gap-4 text-body-sm font-semibold leading-none tracking-[0.28px]",
          variantClasses.label,
        )}
      >
        <span>{t("portal:authenticator.sms.verification.label")}</span>
        <div
          className={clsx(
            "flex w-fit justify-center gap-1",
            variantClasses.container,
          )}
        >
          {Array.from({ length: CONFIRMATION_CODE_LENGTH }).map((_, index) => (
            <input
              key={index}
              className={clsx(
                "text-center text-body-base leading-md tracking-[0.32px] focus:outline-none",
                variantClasses.box,
                hasError &&
                  "border-2 border-border-error text-foreground-error",
                !hasError && variantClasses.input,
              )}
              required
              maxLength={1}
              inputMode="numeric"
              pattern="[0-9]*"
              onKeyDown={(e) => handleKeyDown(e, index)}
              onInput={(e) => handleInput(e, index)}
              onPaste={handlePaste}
              ref={(el) => {
                if (el) {
                  inputRefs.current[index] = el;
                }
              }}
            />
          ))}
        </div>
      </label>
      {hasError && (
        <ErrorMessage name="code" message={error || errors.code?.message} />
      )}
    </fieldset>
  );
};
